declare global {
    interface Window {
        ApplePaySession?: ApplePay.ISession;
        ApplePayError?: ApplePay.Error;
    }
}

export namespace ApplePay {
    export interface ISession {
        STATUS_FAILURE: string;
        STATUS_SUCCESS: string;
        // Indicates whether the device supports Apple Pay.
        canMakePayments(): boolean;
        // Indicates whether the device supports Apple Pay and whether the user has an active card in Wallet.
        canMakePaymentsWithActiveCard(merchantIdentifier: string): Promise<boolean>;
        // Detects whether a web browser supports a particular Apple Pay version.
        supportsVersion(version: number): boolean;
        new (version: number, paymentRequest: IPaymentRequest): ISessionInstance;
    }

    // The error code for this instance.
    // The field name that contains the error on the payment sheet.
    // A localized, user-facing string that describes the error.
    export type Error = new (errorCode: ErrorCode, contactField?: ErrorContactField, message?: string) => IErrorInstance;

    export interface IErrorInstance {
        errorCode: ErrorCode;
        contactField?: ErrorContactField;
        message?: string;
    }

    // The error code that indicates whether an error on the payment sheet is
    // for shipping or billing information, or for another kind of error.
    export enum ErrorCode {
        // Shipping address or contact information is invalid or missing.
        shippingContactInvalid = 'shippingContactInvalid',
        // Billing address information is invalid or missing.
        billingContactInvalid = 'billingContactInvalid',
        // The merchant cannot provide service to the shipping address (for example, can't deliver to a P.O. Box).
        addressUnserviceable = 'addressUnserviceable',
        // An unknown but nonfatal error occurred during payment processing. The user can attempt authorization again.
        unknown = 'unknown'
    }

    // Names of the fields in the shipping or billing contact information, used to locate errors in the payment sheet.
    // When shipping or billing contact information has an error,
    // you use the error code values of either "shippingContactInvalid" or "billingContactInvalid"
    // to indicate which one has the error. You use the contact field to indicate the exact field with the error.
    // The corresponding field in the shipping or billing section of the payment sheet is highlighted.
    export type ErrorContactField = keyof IContact;

    export interface ISessionInstance {
        // An event handler that is called when the payment sheet is displayed.
        onvalidatemerchant: (event: IValidateMerchantEvent) => void;
        // An event handler that is called when a new payment method is selected.
        onpaymentmethodselected: (event: IPaymentMethodSelectedEvent) => void;
        // An event handler that is called when the user has authorized the Apple Pay payment with Touch ID, Face ID, or passcode.
        onpaymentauthorized: (event: IPaymentEvent) => void;
        // An event handler that is called when a shipping contact is selected in the payment sheet.
        onshippingcontactselected: (event: IShippingContactSelectedEvent) => void;
        // An event handler that is called when a shipping method is selected.
        onshippingmethodselected: (event: IShippingMethodSelectedEvent) => void;
        // An event handler that is automatically called when the payment UI is dismissed.
        oncancel: () => void;
        // Completes the validation for a merchant session.
        completeMerchantValidation(merchanSession: any): void;
        // Completes the selection of a payment method with an update.
        completePaymentMethodSelection(update: IPaymentMethodUpdate): void;
        // Completes the selection of a shipping contact with an update.
        completeShippingContactSelection(update: IShippingContactUpdate): void;
        // Completes the selection of a shipping method with an update.
        completeShippingMethodSelection(update: IShippingMethodUpdate): void;
        // Aborts the current Apple Pay session.
        abort(): void;
        // When this method is called, the payment sheet is presented and the merchant validation process is initiated.
        begin(): void;
        // Completes the payment authorization with a result.
        completePayment(result: IPaymentAuthorizationResult): void;
    }

    export interface IValidateMerchantEvent {
        // The URL your server must use to validate itself and obtain a merchant session object.
        validationURL: string;
    }

    export interface IPaymentAuthorizationResult {
        status: string;
        errors?: IErrorInstance[];
    }

    export interface IPaymentMethodSelectedEvent {
        paymentMethod: IPaymentMethod;
    }

    export interface IShippingContactSelectedEvent {
        shippingContact: IContact;
    }

    export interface IShippingMethodSelectedEvent {
        shippingMethod: IShippingMethod;
    }

    export interface IPaymentMethod {
        displayName: string;
        network: string;
        type: PaymentMethodType;
        paymentPass: IPaymentPass;
        billingContact: IContact;
    }

    export interface IPaymentPass {
        primaryAccountIdentifier: string;
        primaryAccountNumberSuffix: string;
        deviceAccountIdentifier?: string;
        deviceAccountNumberSuffix?: string;
        activationState: PaymentPassActivationState;
    }

    export interface IPaymentMethodUpdate {
        // Set the newTotal argument with the total cost of all the purchased items.
        newTotal ?: ILineItem;
        // Use the newLineItems only if you have new or updated costs or discounts, otherwise pass an empty array or null.
        newLineItems?: ILineItem[] | [] | null;
    }

    export interface IPaymentEvent {
        payment: IPayment;
    }

    export interface IPayment {
        token: string;
        billingContact?: IContact;
        shippingContact?: IContact;
    }

    export interface IShippingContactUpdate {
        errors?: IErrorInstance[];
        newShippingMethods?: IShippingMethod[];
        newTotal?: ILineItem;
        newLineItems?: ILineItem[] | [] | null;
    }

    export interface IShippingMethodUpdate {
        newTotal?: ILineItem;
        newLineItems?: ILineItem[] | [] | null;
    }

    export enum PaymentPassActivationState {
        // Active and ready to be used for payment.
        activated = 'activated',
        // Not active but may be activated by the issuer.
        requiresActivation = 'requiresActivation',
        // Not ready for use but activation is in progress.
        activating = 'activating',
        // Not active and can't be activated.
        suspended = 'suspended',
        // Not active because the issuer has disabled the account associated with the device.
        deactivated = 'deactivated'
    }

    export enum LineItemType {
        // A line item representing the known, final cost.
        final = 'final',
        // A line item representing an estimated or unknown cost.
        pending = 'pending'
    }

    export enum MerchantCapabilityType {
        supports3DS = 'supports3DS',
        supportsCredit = 'supportsCredit',
        supportsDebit = 'supportsDebit',
        supportsEMV = 'supportsEMV'
    }

    export enum PaymentMethodType {
        debit = 'debit',
        credit = 'credit',
        prepaid = 'prepaid',
        store = 'store'
    }

    export enum ShippingType {
        shipping = 'shipping',
        delivery = 'delivery',
        storePickup = 'storePickup',
        servicePickup = 'servicePickup'
    }

    export enum ContactField {
        email = 'email',
        name = 'name',
        phone = 'phone',
        postalAddress = 'postalAddress',
        // Contact field values available starting in API version 3:
        phoneticName = 'phoneticName'
    }

    export enum ErrorContext {
        completePayment = 'completePayment',
        completeShippingContactSelection = 'completeShippingContactSelection'
    }

    export interface IPaymentRequest {
        // The supported values for merchantCapabilities are:
        // supports3DS - Required. This value must be supplied.
        // supportsCredit - Optional. If present, only transactions that are categorized as credit cards are allowed.
        // supportsDebit - Optional. If present, only transactions that are categorized as debit cards are allowed.
        // supportsEMV - Include this value only if you support China Union Pay transactions.
        // If both or neither supportsCredit and supportsDebit values are supplied, the transaction allows both credit and debit cards.
        merchantCapabilities: MerchantCapabilityType[];
        // The payment networks supported by the merchant. examples: 'visa', 'amex', 'masterCard' etc
        supportedNetworks: string[];
        countryCode: string;
        requiredBillingContactFields?: ContactField[];
        // If you supply a billing address, you must also request "postalAddress" in requiredBillingContactFields.
        billingContact?: IContact;
        requiredShippingContactFields?: ContactField[];
        shippingContact?: IContact;
        // Use this property to contain additional data as may be appropriate for your website.
        // For example, the application data may be a shopping cart identifier or an order number
        // that your back end will need to identify this transaction.
        // You are responsible for sending the full application data to your server, if needed.
        // The application data string must be Base64-encoded; otherwise, it is ignored.
        applicationData?: string;
        // ISO 3166 country codes
        supportedCountries?: string;
        total: ILineItem;
        // 'totals' - shipping, tax, discount etc, grand total is provided in 'total'
        lineItems?: ILineItem[];
        // ISO 4217
        currencyCode: string;
        // default 'shipping'
        shippingType?: ShippingType[];
        shippingMethods?: IShippingMethod[];
    }

    export interface IContact {
        // A phone number for the contact.
        phoneNumber?: string;
        // An email address for the contact.
        emailAddress?: string;
        // The contact’s given name.
        givenName?: string;
        // The contact’s family name.
        familyName?: string;
        // The phonetic spelling of the contact's given name.
        phoneticGivenName?: string;
        // The phonetic spelling of the contact's family name.
        phoneticFamilyName?: string;
        // The street portion of the address for the contact.
        addressLines?: string[];
        // Additional information associated with the location,
        // typically defined at the city or town level (such as district or neighborhood), in a postal address.
        subLocality?: string;
        // The city for the contact.
        locality?: string;
        // The zip code or postal code, where applicable, for the contact.
        postalCode?: string;
        // The subadministrative area (such as a county or other region) in a postal address.
        subAdministrativeArea?: string;
        // The state for the contact.
        administrativeArea?: string;
        // The name of the country or region for the contact.
        country?: string;
        // The contact’s two-letter ISO 3166 country code.
        countryCode?: string;
    }

    export interface ILineItem {
        label?: string;
        amount?: string;
        type?: LineItemType;
    }

    export interface IShippingMethod {
        label: string;
        detail: string;
        amount: string;
        identifier: string;
    }
}
